import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, RectangleHorizontal, Loader2 } from 'lucide-react';
import AdPlanPicker from '../components/AdPlanPicker';
import { handleAdSaved, adErrorMessage, MY_ADS_URL } from '../utils/adSubmit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import PlateVisual from '../components/plates/PlateVisual';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AddPlateListing() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [plan, setPlan] = useState(null);
  const isAr = language === 'ar';

  // ديناميكيات من الـ schema المركزي (asb606_admin + منصة التجار)
  const [schema, setSchema] = useState(null);
  const [emirates, setEmirates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // اختيار المستخدم
  const [emirate, setEmirate] = useState('');
  const [category, setCategory] = useState('');
  const [codeId, setCodeId] = useState('');
  const [number, setNumber] = useState('');
  // القيم الديناميكية للحقول (price/negotiable/description تُعطى default من الـ schema)
  const [values, setValues] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [schemaRes, codesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/listing-schemas`),
          axios.get(`${BACKEND_URL}/api/plate-codes`),
        ]);
        const s = schemaRes.data?.schemas?.car_plates;
        setSchema(s);
        setEmirates(codesRes.data?.emirates || []);

        // Initialize field defaults from schema
        const initValues = {};
        (s?.fields || []).forEach((f) => {
          if (f.type === 'toggle') initValues[f.name] = f.default || false;
          else if (f.type === 'number') initValues[f.name] = '';
          else initValues[f.name] = '';
        });
        if (editId) {
          const adRes = await axios.get(`${BACKEND_URL}/api/user/ads/${editId}`, { headers: getAuthHeaders() });
          const ad = adRes.data || {};
          const pl = ad.details?.plate || {};
          setEmirate(pl.emirate || ad.emirate || '');
          setCategory(pl.category || ad.plateType || '');
          setCodeId(pl.code_id || ad.plate_code_id || '');
          setNumber(pl.number || ad.plateNumber || '');
          (s?.fields || []).forEach((f) => {
            if (f.name === 'price') initValues.price = ad.price != null ? String(ad.price) : '';
            else if (f.name === 'negotiable') initValues.negotiable = Boolean(ad.is_negotiable);
            else if (f.name === 'description') initValues.description = ad.description || '';
          });
        }
        setValues(initValues);
      } catch {
        toast.error(isAr ? 'تعذر تحميل بيانات اللوحات' : 'Failed to load plate data');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAr, editId]);

  const currentEmirate = useMemo(() => emirates.find((e) => e.id === emirate), [emirates, emirate]);
  const currentCategory = useMemo(() => currentEmirate?.categories?.find((c) => c.id === category), [currentEmirate, category]);
  const currentCode = useMemo(() => currentCategory?.codes?.find((c) => c.id === codeId), [currentCategory, codeId]);

  const maxDigits = useMemo(() => {
    // From schema: "digits_only_max_5" → 5
    const s = schema?.number_input?.sanitize || 'digits_only_max_5';
    const m = s.match(/max_(\d+)/);
    return m ? parseInt(m[1], 10) : 5;
  }, [schema]);

  const handleEmirateChange = (id) => {
    setEmirate(id);
    setCategory('');
    setCodeId('');
  };
  const handleCategoryChange = (id) => {
    setCategory(id);
    setCodeId('');
  };
  const handleNumberChange = (val) => {
    setNumber(val.replace(/\D/g, '').slice(0, maxDigits));
  };
  const setFieldValue = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const vm = schema?.validation_messages || {};
  const vmsg = (key, fallbackAr, fallbackEn) =>
    vm[key]?.[isAr ? 'ar' : 'en'] || (isAr ? fallbackAr : fallbackEn);

  const handleSubmit = async () => {
    if (!emirate) return toast.error(vmsg('emirate', 'يرجى اختيار الإمارة', 'Please select emirate'));
    if (!category) return toast.error(vmsg('category', 'يرجى اختيار الفئة', 'Please select category'));
    if (!codeId) return toast.error(vmsg('code', 'يرجى اختيار الكود', 'Please select code'));

    const regex = new RegExp(schema?.number_input?.regex || `^[0-9]{1,${maxDigits}}$`);
    if (!regex.test(number)) return toast.error(vmsg('number', `رقم اللوحة: أرقام فقط (1-${maxDigits} خانات)`, `Plate number: digits only (1-${maxDigits})`));

    // Validate required schema fields
    for (const f of schema?.fields || []) {
      if (!f.required) continue;
      const v = values[f.name];
      if (f.type === 'number') {
        if (!v || parseFloat(v) <= 0) return toast.error(vmsg(f.name, 'يرجى إدخال سعر صحيح', 'Please enter a valid price'));
      } else if (f.type === 'textarea' || f.type === 'text') {
        if (!v || !String(v).trim()) return toast.error(f[isAr ? 'label_ar' : 'label_en'] + (isAr ? ' مطلوب' : ' is required'));
      }
    }

    if (!isEdit && plan && !plan.ready) return toast.error(isAr ? 'يرجى اختيار مدة الإعلان' : 'Please choose the ad duration');

    setSubmitting(true);
    try {
      const payload = {
        emirate,
        category,
        code_id: codeId,
        code: currentCode?.code || '',
        code_ar: currentCode?.code_ar || currentCode?.code || '',
        show_on_plate: currentCode?.show_on_plate !== false,
        number,
        // Include all dynamic field values
        ...Object.fromEntries(
          Object.entries(values).map(([k, v]) => [
            k,
            typeof v === 'string' && !isNaN(parseFloat(v)) && (schema?.fields || []).find((f) => f.name === k)?.type === 'number'
              ? parseFloat(v)
              : v,
          ])
        ),
      };
      const title = `${currentCode?.code || ''} ${number}`.trim();
      if (isEdit) {
        const res = await axios.put(`${BACKEND_URL}/api/user/ads/plates/${editId}`, payload, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title });
      } else {
        if (plan && !plan.isFree) payload.tier_id = plan.tierId;
        const res = await axios.post(`${BACKEND_URL}/api/user/ads/plate`, payload, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title });
      }
    } catch (err) {
      toast.error(adErrorMessage(err, isAr));
    } finally {
      setSubmitting(false);
    }
  };

  const cardCls = isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white border border-[#E2E8F0]';
  const selectCls = `w-full rounded-md border px-3 py-0 h-10 leading-none text-sm ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-[#E2E8F0]'}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  // Renderer ديناميكي لحقل حسب نوعه في الـ schema
  const renderField = (f) => {
    const label = f[isAr ? 'label_ar' : 'label_en'];
    const val = values[f.name] ?? '';
    if (f.type === 'toggle') {
      return (
        <div key={f.name} className={`flex items-center justify-between rounded-xl border p-4 ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
          <span className="font-medium">{label}</span>
          <Switch checked={!!val} onCheckedChange={(v) => setFieldValue(f.name, v)} data-testid={`toggle-${f.name}`} />
        </div>
      );
    }
    if (f.type === 'number') {
      return (
        <div key={f.name}>
          <Label className="mb-2 block">{label} {f.required && '*'}</Label>
          <div className="relative">
            <Input
              type="number"
              value={val}
              onChange={(e) => setFieldValue(f.name, e.target.value)}
              placeholder="0"
              min={f.min ?? 0}
              data-testid={`plate-field-${f.name}`}
            />
            {f.suffix && <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]">{f.suffix}</span>}
          </div>
        </div>
      );
    }
    // textarea (or default)
    return (
      <div key={f.name}>
        <Label className="mb-2 block">{label} {f.required && '*'}</Label>
        <Textarea
          value={val}
          onChange={(e) => setFieldValue(f.name, e.target.value)}
          rows={f.rows || 3}
          data-testid={`plate-field-${f.name}`}
        />
      </div>
    );
  };

  const digitsCounter = (isAr
    ? (schema?.number_input?.counter_label_ar || `أرقام فقط، من 1 إلى ${maxDigits} خانات ({n}/${maxDigits})`)
    : `Digits only, 1-${maxDigits} characters ({n}/${maxDigits})`
  ).replace('{n}', number.length);

  return (
    <div className={`min-h-screen py-8 px-4 ${isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-[#F8FAFC] text-[#0F172A]'}`} data-testid="add-plate-listing-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(MY_ADS_URL)}
          className="flex items-center gap-2 text-[#1E9BD9] mb-6 hover:underline"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowRight className={`h-4 w-4 ${!isAr ? 'rotate-180' : ''}`} />
          {isAr ? 'العودة إلى إعلاناتي' : 'Back to My Ads'}
        </button>

        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <RectangleHorizontal className="h-6 w-6 text-[#1E9BD9]" />
          {isEdit ? (isAr ? 'تعديل إعلان لوحة سيارة' : 'Edit Car Plate Ad') : (isAr ? 'إضافة إعلان لوحة سيارة' : 'Add Car Plate Ad')}
        </h1>

        {!isEdit && (
          <div className="mb-6">
            <AdPlanPicker category="plates" isAr={isAr} isDarkMode={isDarkMode} value={plan} onChange={setPlan} />
          </div>
        )}

        {/* Live Official Plate Preview */}
        <div
          className={`rounded-2xl p-8 mb-6 flex items-center justify-center min-h-[160px] ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gradient-to-br from-slate-100 to-slate-200'}`}
          data-testid="plate-preview"
        >
          {emirate && category && currentCode ? (
            <PlateVisual
              emirate={emirate}
              category={category}
              code={currentCode.code}
              number={number}
              width={340}
              imageUrl={currentCode.image_url}
              showCodeOnPlate={currentCode.show_on_plate}
            />
          ) : (
            <p className="text-sm text-[#94A3B8]">
              {isAr ? 'اختر الإمارة والفئة والكود لمعاينة اللوحة الرسمية' : 'Select emirate, category and code to preview the official plate'}
            </p>
          )}
        </div>

        <div className={`rounded-2xl p-6 space-y-5 ${cardCls}`}>
          {/* Emirate */}
          <div>
            <Label className="mb-2 block">{isAr ? 'الإمارة' : 'Emirate'} *</Label>
            <select value={emirate} onChange={(e) => handleEmirateChange(e.target.value)} className={selectCls} data-testid="plate-emirate-select">
              <option value="">{isAr ? '— اختر الإمارة —' : '— Select Emirate —'}</option>
              {emirates.map((e) => (
                <option key={e.id} value={e.id}>{isAr ? e.name_ar : e.name_en}</option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <Label className="mb-2 block">{isAr ? 'الفئة' : 'Category'} *</Label>
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={selectCls}
              disabled={!emirate}
              data-testid="plate-category-select"
            >
              <option value="">{isAr ? '— اختر الفئة —' : '— Select Category —'}</option>
              {(currentEmirate?.categories || []).map((c) => (
                <option key={c.id} value={c.id}>{isAr ? c.name_ar : c.name_en}</option>
              ))}
            </select>
          </div>

          {/* Code */}
          <div>
            <Label className="mb-2 block">{isAr ? 'الكود' : 'Code'} *</Label>
            <select
              value={codeId}
              onChange={(e) => setCodeId(e.target.value)}
              className={selectCls}
              disabled={!category}
              data-testid="plate-code-select"
            >
              <option value="">
                {(currentCategory?.codes || []).length === 0 && category
                  ? (isAr ? '— لا توجد أكواد متاحة حالياً —' : '— No codes available —')
                  : (isAr ? '— اختر الكود —' : '— Select Code —')}
              </option>
              {(currentCategory?.codes || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {isAr ? (c.code_ar || c.code) : c.code}
                  {(isAr ? c.label_ar : c.label_en) ? ` — ${isAr ? c.label_ar : c.label_en}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Number (dynamic from schema) */}
          <div>
            <Label className="mb-2 block">{isAr ? 'رقم اللوحة' : 'Plate Number'} *</Label>
            <Input
              value={number}
              onChange={(e) => handleNumberChange(e.target.value)}
              placeholder="30026"
              className="tracking-widest font-bold text-lg"
              dir={schema?.number_input?.direction || 'ltr'}
              data-testid="plate-number-input"
            />
            <p className="text-xs text-[#94A3B8] mt-1">{digitsCounter}</p>
          </div>

          {/* Dynamic fields from schema (price, negotiable, description, ...) */}
          {(schema?.fields || []).map(renderField)}

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#1E9BD9] hover:bg-[#1786be] text-white h-11 text-base"
            data-testid="submit-plate-ad-btn"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isEdit ? (isAr ? 'حفظ التعديلات' : 'Save Changes') : plan && !plan.isFree && plan.ready ? (isAr ? `نشر والدفع (${plan.fee} ${plan.currency})` : `Publish & Pay (${plan.fee} ${plan.currency})`) : (isAr ? 'نشر الإعلان' : 'Publish Ad')}
          </Button>
        </div>
      </div>
    </div>
  );
}
